import React, { useState, useRef, useEffect } from 'react';
import './VendorAssessmentForm.css';
import { trackVendorAssessmentSubmit, trackVendorAssessmentError } from '../utils/analytics';

// Verbatim select values from the brief (kept in sync with the backend enums
// added in the functional phase: INDUSTRY_ENUM / PROMPTING_ENUM).
const INDUSTRY_OPTIONS = [
  'Healthtech',
  'Fintech',
  'Insurtech',
  'SaaS (regulated B2B)',
  'Other regulated',
  'Other'
];

const PROMPTING_OPTIONS = [
  'Evaluating this specific vendor right now',
  'Building a repeatable vendor review process',
  'Customer / auditor is asking us to assess',
  'Just exploring'
];

// Data-scope signals - these gate which of the 43 master requirements activate.
// Wording mirrors the skill's Stage 2 scoping interview (vendor-profile-questions).
const DATA_SCOPE_OPTIONS = [
  { value: 'pii', label: 'Personal data (PII)', hint: 'Names, emails, customer records, anything identifying a person' },
  { value: 'payment', label: 'Payment / financial data', hint: 'Card numbers, bank details, transaction records (PCI scope)' },
  { value: 'health', label: 'Health data (PHI)', hint: 'Diagnoses, medical records, claims - HIPAA / Art. 9 scope' },
  { value: 'source_code', label: 'Source code / proprietary IP', hint: 'Code, algorithms, model weights, internal docs' },
  { value: 'credentials', label: 'Credentials / secrets', hint: 'API keys, tokens, passwords, SSH keys' },
  { value: 'public_only', label: 'None - public / non-sensitive only', hint: 'Only public, fully anonymized, or synthetic data' }
];

const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function VendorAssessmentForm() {
  const [vendorName, setVendorName] = useState('');
  const [email, setEmail] = useState('');
  const [industry, setIndustry] = useState('');
  const [useCase, setUseCase] = useState('');
  const [dataScope, setDataScope] = useState([]);
  const [dataScopeOpen, setDataScopeOpen] = useState(false);
  const [prompting, setPrompting] = useState('');
  const [company, setCompany] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const dataScopeRef = useRef(null);

  // Required fields are complete and email is well-formed.
  const isValid =
    vendorName.trim() !== '' &&
    validateEmail(email) &&
    industry !== '' &&
    useCase.trim() !== '' &&
    prompting !== '';

  // Close the data-scope dropdown on outside click or Escape.
  useEffect(() => {
    if (!dataScopeOpen) return undefined;
    const onClick = (e) => {
      if (dataScopeRef.current && !dataScopeRef.current.contains(e.target)) {
        setDataScopeOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setDataScopeOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [dataScopeOpen]);

  const toggleDataScope = (value) => {
    setDataScope((prev) => {
      // "public_only" is mutually exclusive with the sensitive-data options.
      if (value === 'public_only') {
        return prev.includes('public_only') ? [] : ['public_only'];
      }
      const next = prev.filter((v) => v !== 'public_only');
      return next.includes(value) ? next.filter((v) => v !== value) : [...next, value];
    });
  };

  // Labels for the selected values, used to render chips in the collapsed trigger.
  const selectedLabels = DATA_SCOPE_OPTIONS
    .filter((opt) => dataScope.includes(opt.value))
    .map((opt) => opt.label);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || isLoading) {
      setErrorMessage('Please complete the required fields with a valid email.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/api/vendor-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendorName: vendorName.trim(),
          email: email.trim(),
          industry,
          useCase: useCase.trim(),
          dataScope,
          prompting,
          company: company.trim() || undefined,
          referrer: typeof window !== 'undefined' ? window.location.href : undefined
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const data = await response.json().catch(() => ({}));

      if (response.ok || response.status === 200) {
        setIsSuccess(true);
        trackVendorAssessmentSubmit();
      } else if (response.status >= 400 && response.status < 500) {
        setErrorMessage(data.error || data.message || 'Please check your input and try again.');
        trackVendorAssessmentError(`Validation Error: ${data.code || 'Unknown'}`);
      } else if (response.status >= 500) {
        setErrorMessage('Server error. Please try again in a few moments.');
        trackVendorAssessmentError(`Server Error: ${response.status}`);
      } else {
        setErrorMessage('Unexpected error. Please try again.');
        trackVendorAssessmentError(`Unexpected Status: ${response.status}`);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        setErrorMessage('Request timed out. Please check your connection and try again.');
        trackVendorAssessmentError('Request Timeout');
      } else if (err.message && (err.message.includes('Failed to fetch') || err.message.includes('NetworkError'))) {
        setErrorMessage('Cannot connect to server. Please check your internet connection.');
        trackVendorAssessmentError('Network Error');
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
        trackVendorAssessmentError(`Unexpected Error: ${err.name || 'Unknown'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="va-form-card">
        <div className="va-form-success">
          <div className="va-success-icon">✓</div>
          <h3>Request received</h3>
          <p>
            Your assessment of <strong>{vendorName.trim()}</strong> is on the way. Watch your inbox -
            both reports - interactive and DOCX - arrive within 24 hours. We've sent a confirmation to {email.trim()}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="va-form-card">
      <form onSubmit={handleSubmit} className="va-form" noValidate>
        <div className="va-field">
          <label className="va-label" htmlFor="va-vendor">Vendor name</label>
          <input
            id="va-vendor"
            type="text"
            className="va-input"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            placeholder="e.g. Cursor, Decagon, FlowScribe AI"
            maxLength={120}
            required
          />
          <p className="va-form-helper">The AI vendor you want assessed. URL helps if the name is ambiguous.</p>
        </div>

        <div className="va-field">
          <label className="va-label" htmlFor="va-email">Work email</label>
          <input
            id="va-email"
            type="email"
            className="va-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            maxLength={254}
            required
          />
          <p className="va-form-helper">We'll send both reports here. No newsletters, no spam.</p>
        </div>

        <div className="va-field">
          <label className="va-label" htmlFor="va-industry">Your industry</label>
          <select
            id="va-industry"
            className="va-input va-select"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            required
          >
            <option value="" disabled>Select your industry</option>
            {INDUSTRY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <p className="va-form-helper">Determines which frameworks weight more heavily in scoping.</p>
        </div>

        <div className="va-field">
          <label className="va-label" htmlFor="va-usecase">Brief use case</label>
          <textarea
            id="va-usecase"
            className="va-input va-textarea"
            value={useCase}
            onChange={(e) => setUseCase(e.target.value)}
            placeholder="One line on what you'll use this vendor for. e.g. 'AI note-taker for clinical care team meetings' or 'AI coding assistant for our backend engineers'"
            rows={3}
            maxLength={2000}
            required
          />
          <p className="va-form-helper">Shapes the scoping interview. Be specific about data and users.</p>
        </div>

        <div className="va-field">
          <span className="va-label" id="va-datascope-label">What data will this vendor touch?</span>
          <div className="va-multiselect" ref={dataScopeRef}>
            <button
              type="button"
              className={`va-input va-multiselect-trigger ${dataScopeOpen ? 'open' : ''}`}
              onClick={() => setDataScopeOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={dataScopeOpen}
              aria-labelledby="va-datascope-label"
            >
              {selectedLabels.length === 0 ? (
                <span className="va-multiselect-placeholder">Select data types…</span>
              ) : (
                <span className="va-multiselect-chips">
                  {selectedLabels.map((label) => (
                    <span key={label} className="va-chip">{label}</span>
                  ))}
                </span>
              )}
              <span className="va-multiselect-caret" aria-hidden="true">▾</span>
            </button>

            {dataScopeOpen && (
              <div className="va-multiselect-panel" role="listbox" aria-multiselectable="true">
                {DATA_SCOPE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="va-checkbox" role="option" aria-selected={dataScope.includes(opt.value)}>
                    <input
                      type="checkbox"
                      checked={dataScope.includes(opt.value)}
                      onChange={() => toggleDataScope(opt.value)}
                    />
                    <span className="va-checkbox-body">
                      <span className="va-checkbox-label">{opt.label}</span>
                      <span className="va-checkbox-hint">{opt.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <p className="va-form-helper">
            Select all that apply. This determines which of the 43 master requirements activate. When
            unsure, we apply the conservative default and flag it.
          </p>
        </div>

        <div className="va-field">
          <label className="va-label" htmlFor="va-prompting">What's prompting this?</label>
          <select
            id="va-prompting"
            className="va-input va-select"
            value={prompting}
            onChange={(e) => setPrompting(e.target.value)}
            required
          >
            <option value="" disabled>Select one</option>
            {PROMPTING_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <p className="va-form-helper">No wrong answer. Helps us tailor the cover note.</p>
        </div>

        <div className="va-field">
          <label className="va-label" htmlFor="va-company">
            Company <span className="va-optional">(optional)</span>
          </label>
          <input
            id="va-company"
            type="text"
            className="va-input"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Where you work"
            maxLength={100}
          />
        </div>

        {errorMessage && <p className="va-error-message">{errorMessage}</p>}

        <button
          type="submit"
          className="va-submit-button"
          disabled={!isValid || isLoading}
        >
          {isLoading ? 'Sending…' : 'Send me the snapshot →'}
        </button>
        <p className="va-submit-microcopy">
          Two reports within 24 hours · One vendor free
        </p>
      </form>
    </div>
  );
}

export default VendorAssessmentForm;
